import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  Wheat,
  Users,
  HeartHandshake,
  Truck,
  UserCog,
  BarChart3,
  Settings,
  Sparkles,
  Wallet,
  Bike,
  Factory,
  Brain,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  TrendingUp,
  Pencil,
  Upload,
  ImageIcon,
  X,
} from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useCreatorConfig } from "../contexts/CreatorConfigContext";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import api from "../lib/api";

const MAX_LOGO_BYTES = 300 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, key: "dashboard", testId: "nav-dashboard" },
  { to: "/orders", icon: ShoppingBag, key: "orders", testId: "nav-orders", perm: "orders.view" },
  { to: "/products", icon: Package, key: "products", testId: "nav-products", perm: "products.view" },
  { to: "/materials", icon: Wheat, key: "materials", testId: "nav-materials", perm: "materials.view" },
  { to: "/production", icon: Factory, key: "production", testId: "nav-production", perm: "stock.in" },
  { to: "/customers", icon: Users, key: "customers", testId: "nav-customers", perm: "customers.view" },
  { to: "/customer-care", icon: HeartHandshake, key: "customerCare", testId: "nav-customer-care", perm: "customers.view" },
  { to: "/insights", icon: Brain, key: "insights", testId: "nav-insights", perm: "reports.view" },
  { to: "/suppliers", icon: Truck, key: "suppliers", testId: "nav-suppliers", perm: "suppliers.view" },
  { to: "/debts", icon: Wallet, key: "debts", testId: "nav-debts", perm: "debts.view" },
  { to: "/delivery", icon: Bike, key: "delivery", testId: "nav-delivery", perm: "delivery.view" },
  { to: "/users", icon: UserCog, key: "users", testId: "nav-users", perm: "users.view" },
  { to: "/reports", icon: BarChart3, key: "reports", testId: "nav-reports", perm: "reports.view" },
  { to: "/finance", icon: TrendingUp, key: "finance", testId: "nav-finance", perm: "reports.view" },
  { to: "/settings", icon: Settings, key: "settings", testId: "nav-settings" },
];

function hasPermission(user, perm) {
  if (!perm) return true;
  if (!user) return false;
  if (user.role === "admin") return true;
  const perms = user.permissions_effective || user.permissions || [];
  return perms.includes(perm);
}

export default function Sidebar({ mobileOpen, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Brand state
  const [logoUrl, setLogoUrl] = useState("");
  const [shopName, setShopName] = useState("Bánh Bao");
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const logoFileRef = useRef(null);
  const editRef = useRef(null);

  // ============================================================
  // CREATOR CONFIG — Dùng context (đã fetch 1 lần ở App.js)
  // ============================================================
  const { config: creatorConfig } = useCreatorConfig();
  // ============================================================

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (data.logo_url) setLogoUrl(data.logo_url);
      if (data.shop_name) setShopName(data.shop_name);
    }).catch(() => {});
  }, []);

  // Đóng popup khi click ngoài
  useEffect(() => {
    const handler = (e) => {
      if (editRef.current && !editRef.current.contains(e.target)) setEditOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) { alert("Logo quá lớn. Tối đa 300KB."); return; }
    try {
      const b64 = await fileToBase64(file);
      setLogoUrl(b64);
    } catch { alert("Không đọc được file."); }
    finally { if (logoFileRef.current) logoFileRef.current.value = ""; }
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      const { data: current } = await api.get("/settings");
      await api.put("/settings", { ...current, logo_url: logoUrl, shop_name: editName || shopName });
      setShopName(editName || shopName);
      setEditOpen(false);
    } catch { alert("Lưu thất bại."); }
    finally { setSaving(false); }
  };

  const openEdit = () => {
    setEditName(shopName);
    setEditOpen(true);
  };

  // 1. Trạng thái đóng/mở cụm menu ẩn bớt
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // 2. Trạng thái đóng/mở panel "Cài đặt chọn menu ẩn"
  const [isConfiguring, setIsConfiguring] = useState(false);

  // 3. Lấy danh sách các key được cấu hình là "ẩn/nâng cao" từ localStorage
  const [advancedKeys, setAdvancedKeys] = useState(() => {
    const saved = localStorage.getItem("sidebar_advanced_keys");
    return saved ? JSON.parse(saved) : ["insights", "suppliers", "debts", "reports"];
  });

  // Lọc theo quyền user + ẩn theo Creator config
  const allowedItems = NAV_ITEMS
    .filter((item) => hasPermission(user, item.perm))
    // Ẩn trang Settings với non-admin nếu Creator bật hide_admin_settings
    .filter((item) => {
      if (item.key === "settings" && creatorConfig.hide_admin_settings) {
        return user?.role === "admin";
      }
      return true;
    })
    // Ẩn các trang Creator Panel đã cấu hình ẩn
    .filter((item) => !creatorConfig.hidden_nav.includes(item.key));

  // Phân loại item dựa trên cấu hình tùy chọn của Admin trong localStorage
  const mainItems = allowedItems.filter(item => !advancedKeys.includes(item.key));
  const advancedItems = allowedItems.filter(item => advancedKeys.includes(item.key));

  // Hàm xử lý khi admin tích chọn/bỏ chọn một tab muốn ẩn đi
  const handleToggleKeyConfig = (key) => {
    let updatedKeys;
    if (advancedKeys.includes(key)) {
      updatedKeys = advancedKeys.filter(k => k !== key);
    } else {
      updatedKeys = [...advancedKeys, key];
    }
    setAdvancedKeys(updatedKeys);
    localStorage.setItem("sidebar_advanced_keys", JSON.stringify(updatedKeys));
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={onClose}
        data-testid={item.testId}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
            isActive
              ? "bg-bamboo text-white shadow-sm"
              : "text-ink-secondary hover:bg-cream-dark hover:text-ink"
          )
        }
      >
        <Icon size={17} strokeWidth={2} />
        <span>{t(`nav.${item.key}`)}</span>
      </NavLink>
    );
  };

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={onClose} data-testid="sidebar-backdrop" />
      )}
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 h-screen w-64 bg-cream border-r border-border flex flex-col transition-transform duration-200",
          "md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* LOGO / BRAND */}
        <div className="px-5 py-5 border-b border-border/70 relative" ref={editRef}>
          <div className="flex items-center gap-2.5">
            {/* Logo: click vào dashboard */}
            <div
              className="relative cursor-pointer shrink-0"
              onClick={() => navigate("/dashboard")}
              data-testid="sidebar-logo"
            >
              <div className="w-10 h-10 rounded-lg bg-bamboo flex items-center justify-center text-white shadow-sm overflow-hidden">
                {logoUrl
                  ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                  : <Sparkles size={20} strokeWidth={2.2} />}
              </div>
            </div>

            {/* Tên tiệm */}
            <div className="leading-tight flex-1 min-w-0 cursor-pointer" onClick={() => navigate("/dashboard")}>
              <div className="font-heading font-bold text-base text-ink truncate">{shopName}</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted">Admin Panel</div>
            </div>

            {/* Nút bút chì — chỉ admin */}
            {user?.role === "admin" && (
              <button
                onClick={openEdit}
                title="Chỉnh logo & tên tiệm"
                className={cn(
                  "p-1.5 rounded-md text-ink-muted hover:bg-cream-dark hover:text-bamboo transition-colors shrink-0",
                  editOpen && "bg-cream-dark text-bamboo"
                )}
              >
                <Pencil size={14} />
              </button>
            )}
          </div>

          {/* Popup chỉnh logo + tên */}
          {editOpen && (
            <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-white border border-border rounded-xl shadow-xl p-4 space-y-3 animate-fade-in">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-ink">Chỉnh logo & tên tiệm</span>
                <button onClick={() => setEditOpen(false)} className="text-ink-muted hover:text-ink">
                  <X size={14} />
                </button>
              </div>

              {/* Logo preview + upload */}
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg border border-border bg-cream/50 overflow-hidden flex items-center justify-center shrink-0">
                  {logoUrl
                    ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                    : <ImageIcon size={20} className="text-ink-muted" />}
                </div>
                <div className="space-y-1.5">
                  <input ref={logoFileRef} type="file" accept="image/*" onChange={onLogoFile} className="hidden" />
                  <button
                    type="button"
                    onClick={() => logoFileRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-cream transition-colors"
                  >
                    <Upload size={12} /> Đổi logo
                  </button>
                  {logoUrl && (
                    <button
                      type="button"
                      onClick={() => setLogoUrl("")}
                      className="text-[11px] text-red-500 hover:underline"
                    >
                      Xóa logo
                    </button>
                  )}
                  <div className="text-[10px] text-ink-muted">Tối đa 300KB</div>
                </div>
              </div>

              {/* Tên tiệm */}
              <div>
                <label className="text-[11px] font-medium text-ink-muted block mb-1">Tên tiệm</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full text-sm px-2.5 py-1.5 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-bamboo/40"
                  placeholder="Tên tiệm..."
                />
              </div>

              {/* Nút lưu */}
              <button
                onClick={saveBrand}
                disabled={saving}
                className="w-full py-1.5 rounded-md bg-bamboo text-white text-xs font-semibold hover:bg-bamboo/90 transition-colors disabled:opacity-60"
              >
                {saving ? "Đang lưu..." : "Lưu"}
              </button>
            </div>
          )}
        </div>

        {/* NAVIGATION MENUS */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          
          {/* PANEL TÙY CHỌN CÀI ĐẶT (Hiện ra khi bấm vào nút Cấu hình) */}
          {isConfiguring && (
            <div className="mb-4 p-3 bg-cream-dark/60 rounded-lg border border-border/80 text-xs space-y-2">
              <div className="font-bold text-ink flex justify-between items-center">
                <span>⚙️ Tùy chọn Tab ẩn bớt:</span>
                <button 
                  onClick={() => setIsConfiguring(false)} 
                  className="px-2 py-0.5 bg-bamboo text-white rounded text-[10px] font-semibold"
                >
                  Xong
                </button>
              </div>
              <p className="text-[10px] text-ink-muted italic">(Tích chọn để đưa tab vào mục thu gọn)</p>
              <div className="max-h-48 overflow-y-auto space-y-1.5 pt-1">
                {allowedItems.map((item) => (
                  <label key={item.key} className="flex items-center gap-2 cursor-pointer text-ink-secondary hover:text-ink">
                    <input
                      type="checkbox"
                      checked={advancedKeys.includes(item.key)}
                      onChange={() => handleToggleKeyConfig(item.key)}
                      className="rounded border-border text-bamboo focus:ring-bamboo w-3.5 h-3.5"
                    />
                    <span>{t(`nav.${item.key}`)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* CHỨC NĂNG CHÍNH */}
          {mainItems.map(renderNavItem)}

          {/* NÚT TẮT/MỞ VÀ NÚT CÀI ĐẶT MENU */}
          <div className="pt-2 mt-2 border-t border-border/40 flex items-center gap-1">
            {advancedItems.length > 0 && (
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex-1 flex items-center justify-between px-3 py-1.5 rounded-md text-xs font-semibold text-ink-muted hover:bg-cream-dark hover:text-ink transition-colors"
              >
                <span>{showAdvanced ? "▴ Ẩn bớt chức năng" : "▾ Xem thêm chức năng"}</span>
                {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            )}
            
            {/* Nút bánh răng để kích hoạt chế độ Cài đặt tùy chọn ẩn/hiện */}
            <button
              onClick={() => setIsConfiguring(!isConfiguring)}
              title="Cấu hình danh sách tab ẩn"
              className={cn(
                "p-1.5 rounded-md text-ink-muted hover:bg-cream-dark hover:text-ink transition-colors",
                isConfiguring && "bg-cream-dark text-bamboo"
              )}
            >
              <SlidersHorizontal size={14} />
            </button>
          </div>

          {/* CHỨC NĂNG ÍT DÙNG (CHỈ HIỆN KHI MỞ) */}
          {showAdvanced && advancedItems.map(renderNavItem)}
        </nav>

        {/* FOOTER — hiện watermark Creator nếu được bật */}
        <div className="px-4 py-3 border-t border-border/70 text-[11px] text-ink-muted">
          {creatorConfig.creator_watermark_visible && creatorConfig.creator_watermark ? (
            <div className="flex flex-col gap-0.5">
              <span>v2.0 · {t("tagline")}</span>
              <span className="text-[10px] text-ink-muted/60 truncate">{creatorConfig.creator_watermark}</span>
            </div>
          ) : (
            <span>v2.0 · {t("tagline")}</span>
          )}
        </div>
      </aside>
    </>
  );
}